import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Phone, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEO from "@/components/SEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize";
import { iconMap, type ServiceMeta } from "@/lib/serviceMeta";

const workProcess = [
  { step: 1, title: "פנייה ראשונית", desc: "מספרים לנו מה הבעיה – בטלפון, וואטסאפ או טופס באתר" },
  { step: 2, title: "סקירה מקצועית", desc: "מגיעים לביקור באתר, בודקים ומאבחנים את המצב" },
  { step: 3, title: "הצעת מחיר", desc: "מקבלים הצעת מחיר מפורטת עם לוח זמנים ברור" },
  { step: 4, title: "ביצוע ואחריות", desc: "מבצעים את העבודה ונותנים אחריות מלאה על התוצאה" },
];

const Services = () => {
  const processRef = useScrollReveal();

  const { data: services = [] } = useQuery({
    queryKey: ["posts", "services"],
    queryFn: async () => {
      const { data } = await supabase
        .from("posts")
        .select("*")
        .eq("section", "services")
        .order("sort_order");
      return data || [];
    },
  });

  const servicesRef = useScrollReveal([services]);

  return (
    <div className="min-h-screen">
      <SEO
        title="שירותי גגות, איטום ופחחות בחיפה והקריות"
        description="תיקון גג רעפים, החלפת רעפים, איטום גגות שטוחים וזיפות, פחחות גגות, התקנת מרזבים וניקוז גגות בחיפה והקריות. הצעת מחיר חינם."
        path="/services"
      />
      <Header />
      <main id="main">

      {/* Hero */}
      <section className="bg-dark pt-28 pb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark-foreground mb-4">
            השירותים <span className="text-primary">שלנו</span>
          </h1>
          <p className="text-lg text-dark-foreground/90 max-w-2xl mx-auto leading-relaxed">
            מגוון פתרונות מקצועיים לגגות ופחחות – עם 13 שנות ניסיון, ציוד מתקדם ואחריות מלאה על כל עבודה
          </p>
          <div className="mt-6">
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-gold-light font-bold">
              <Link to="/">
                <ArrowLeft size={16} />
                חזרה לדף הבית
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section ref={servicesRef} className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-5xl space-y-20">
          {services.map((service, i) => {
            const meta = service.metadata as ServiceMeta;
            const IconComp = meta?.icon ? iconMap[meta.icon] : undefined;
            const features: string[] = meta?.features ?? [];

            return (
              <div
                key={service.id}
                className={`animate-on-scroll flex flex-col ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-8 lg:gap-12 items-center`}
                style={{ transitionDelay: "100ms" }}
              >
                {/* Image */}
                <div className="w-full lg:w-2/5 flex-shrink-0">
                  <div className="aspect-[4/3] rounded-2xl bg-dark overflow-hidden relative group">
                    {service.image_url ? (
                      <img src={service.image_url} alt={`${service.title} – אור בגג`} loading="lazy" decoding="async" width={800} height={600} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        {IconComp && <IconComp className="text-primary/30" size={80} />}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-300" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {IconComp && (
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <IconComp className="text-primary" size={22} />
                      </div>
                    )}
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">{service.title}</h2>
                  </div>
                  {service.subtitle && <p className="text-primary font-medium mb-4">{service.subtitle}</p>}
                  {service.content_html && (
                    <div className="text-muted-foreground leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.content_html) }} />
                  )}

                  {features.length > 0 && (
                    <ul className="space-y-2 mb-6">
                      {features.map((feat, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckCircle2 className="text-primary mt-0.5 flex-shrink-0" size={18} />
                          <span className="text-foreground">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button asChild className="bg-primary text-primary-foreground hover:bg-gold-light font-bold">
                    <a href="/#contact">קבלו הצעת מחיר</a>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Separator />

      {/* Work Process */}
      <section ref={processRef} className="py-20 bg-secondary/50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              איך זה <span className="text-primary">עובד?</span>
            </h2>
            <p className="text-muted-foreground text-lg">תהליך עבודה פשוט ושקוף מתחילתו ועד סופו</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workProcess.map((item) => (
              <Card key={item.step} className="animate-on-scroll text-center border-border bg-card" style={{ transitionDelay: `${item.step * 100}ms` }}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-dark">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-dark-foreground mb-4">
            מוכנים <span className="text-primary">להתחיל?</span>
          </h2>
          <p className="text-dark-foreground/90 text-lg mb-8 max-w-xl mx-auto">
            צרו קשר עוד היום לתיאום ביקור מקצועי – ונפתור לכם כל בעיית גג
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold">
              <a href="/#contact">תיאום ביקור מקצועי</a>
            </Button>
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold">
              <a href="tel:+972545725681">
                <Phone size={20} />
                054-572-5681
              </a>
            </Button>
          </div>
        </div>
      </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Services;
